import { router, useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetDonationImpact } from '@/api/hooks/donationHooks';
import { Impact as ImpactType } from '@/api/models/impactModels';
import DotsLoader from '@/components/DotsLoader';
import { ImpactCard, ImpactCardColumn } from '@/components/impact/ImpactCard';
import ImpactMap from '@/components/impact/ImpactMap';
import ImpactPopUp from '@/components/impact/ImpactPopUp';
import Text from '@/components/Text';
import PlatformContainer from '@/components/web/PlatformContainer';
import { useAuth } from '@/contexts/authContext';
import { useInfoModal } from '@/contexts/infoModalContext';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';
import HeaderBar from '@/components/HeaderBar';

const Impact = () => {
  const { state, isBrowseOnlyUser } = useAuth();

  const { width: screenWidth } = useWindowDimensions();

  const colors = useColor();

  const { showInfo } = useInfoModal();

  const { data: impactData, isPending, refetch } = useGetDonationImpact({ userId: state.user?.user?.id ?? '' });
  const [showDonation, setShowDonation] = useState<boolean>(false);
  const [currentDonation, setCurrentDonation] = useState<ImpactType | null>(null);

  useFocusEffect(
    useCallback(() => {
      if (state.user?.user?.id) refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  useEffect(() => {
    if (!state.authReady) return;

    if (isBrowseOnlyUser) {
      showInfo({
        type: 'full',
        title: registerTitle,
        subtitle: registerSubtitle,
        buttonLabel: registerButtonLabel,
        showClose: false,
        closeOnTapOutside: false,
        onButtonPress: () => {
          router.navigate('/');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBrowseOnlyUser, state.authReady]);

  const handleDonationPress = (item: ImpactType) => {
    setCurrentDonation(item);
    setShowDonation(true);
  };

  //Locales
  const registerTitle = i18n.t('register_modal.title');
  const registerSubtitle = i18n.t('register_modal.subtitle');
  const registerButtonLabel = i18n.t('register_modal.button_label');

  if (isBrowseOnlyUser) return null;

  return (
    <PlatformContainer contentCustomClassnames={'flex flex-col'}>
      <SafeAreaView edges={Platform.OS === 'ios' ? ['top'] : []} style={styles.flexOne}>
        {/* md breakpoint --> 768 px */}
        {Platform.OS !== 'web' || screenWidth < 768 ? (
          <HeaderBar screenTitle={i18n.t('bottom_tab.impact')} />
        ) : (
          <View style={styles.topBar}>
            <Text style={[styles.title, { color: colors.titleText }]}>{i18n.t('bottom_tab.impact')}</Text>
          </View>
        )}
        <View style={[styles.impactContainer, Platform.OS !== 'web' && { paddingHorizontal: 18 }]}>
          {isPending ? (
            <View style={styles.centeredContent}>
              <DotsLoader />
            </View>
          ) : impactData ? (
            <>
              <ImpactCard style={styles.impactCard}>
                <ImpactCardColumn
                  qty={impactData?.groupByType.charities.n}
                  title={i18n.t('profile.impact.charities')}
                  amount={impactData?.groupByType.charities.totalAmount}
                />
                <ImpactCardColumn
                  qty={impactData?.groupByType.church.n}
                  title={i18n.t('profile.impact.churches')}
                  amount={impactData?.groupByType.church.totalAmount}
                />
              </ImpactCard>
              <ImpactPopUp
                data={currentDonation}
                showPopUp={showDonation}
                dismissPopUp={() => setShowDonation(false)}
              />
              <ImpactMap showDonation={handleDonationPress} />
            </>
          ) : (
            <Text>No data</Text>
          )}
        </View>
      </SafeAreaView>
    </PlatformContainer>
  );
};

const styles = StyleSheet.create({
  centeredContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  },

  impactContainer: {
    flex: 1,
    height: '100%'
  },

  impactCard: {
    marginTop: 10,
    marginBottom: 25
  },

  mainNavBarContainer: {
    paddingVertical: 10,
    paddingHorizontal: 18
  },

  topBar: {
    height: 38,
    flexDirection: 'row',
    alignItems: 'center'
  },

  logo: {
    height: 20,
    width: 20,
    paddingVertical: 20
  },

  title: {
    flex: 1,
    textTransform: 'capitalize',
    fontWeight: '700',
    fontSize: 20,
    textAlign: 'center'
  },

  iconsContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },

  iconColor: {
    color: '#808080'
  },

  notificationIcon: {
    marginLeft: 15
  },

  tabBarStyle: {
    width: 290,
    alignSelf: 'center',
    marginBottom: 20
  },

  flexOne: {
    flex: 1
  }
});

export default Impact;
